import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageSearch, X } from 'lucide-react';
import { QuantityStepper } from '@/components/products/QuantityStepper';
import { useCart } from '@/lib/cart-context';

export interface RequestProductDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pré-remplit le libellé — la recherche qui n'a rien donné, typiquement. */
  initialLabel?: string;
  /** Notifie le parent après l'ajout (bandeau de confirmation, ouverture du panier…). */
  onAdded?: (label: string) => void;
}

/**
 * « Je ne trouve pas ce produit » — le libellé + la quantité, rien d'autre.
 *
 * Volontairement plus court qu'un formulaire produit : ce qu'on demande à
 * l'électricien, c'est ce qu'il dirait au comptoir. La précision optionnelle
 * (marque, référence, calibre) évite l'aller-retour quand elle est donnée,
 * mais ne bloque jamais l'envoi.
 */
export function RequestProductDialog({
  open,
  onClose,
  initialLabel,
  onAdded,
}: RequestProductDialogProps) {
  const { t } = useTranslation();
  const { addRequest } = useCart();
  const [label, setLabel] = useState(initialLabel ?? '');
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const labelRef = useRef<HTMLInputElement>(null);

  // Réinitialisation à CHAQUE ouverture : le dialogue reste monté, sans quoi
  // une deuxième demande repartirait avec la saisie de la première.
  useEffect(() => {
    if (!open) return;
    setLabel(initialLabel ?? '');
    setQuantity(1);
    setNote('');
    setTouched(false);
    const id = window.setTimeout(() => labelRef.current?.focus(), 60);
    return () => window.clearTimeout(id);
  }, [open, initialLabel]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const trimmed = label.trim();
  const invalid = trimmed.length === 0;

  const submit = () => {
    setTouched(true);
    if (invalid) {
      labelRef.current?.focus();
      return;
    }
    addRequest({ label: trimmed, quantity, note });
    onAdded?.(trimmed);
    onClose();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-[60] pro-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed inset-0 z-[61] flex items-end sm:items-center justify-center p-0 sm:p-4 pointer-events-none">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="request-product-title"
          className="pointer-events-auto w-full sm:max-w-[480px] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl pro-scale-in overflow-hidden"
        >
          {/* En-tête : on nomme le geste, pas l'objet — « demander », pas « formulaire ». */}
          <div
            className="px-5 py-4 flex items-start gap-3"
            style={{ borderBottom: '1px solid var(--pro-line)' }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'var(--pro-primary-subtle)' }}
            >
              <PackageSearch
                className="w-5 h-5"
                style={{ color: 'var(--pro-primary)' }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id="request-product-title"
                className="text-[17px] font-semibold leading-tight"
                style={{ color: 'var(--pro-text-dark)', fontFamily: 'Figtree' }}
              >
                {t('pro.request.title', 'Demander un produit')}
              </h2>
              <p
                className="text-[13px] mt-0.5"
                style={{ color: 'var(--pro-text-grey)' }}
              >
                {t(
                  'pro.request.subtitle',
                  "Pas au catalogue ? Décrivez-le, on vous répond sur le devis.",
                )}
              </p>
            </div>
            <button
              type="button"
              aria-label={t('pro.request.close', 'Fermer')}
              onClick={onClose}
              className="w-9 h-9 rounded-lg hover:bg-neutral-100 flex items-center justify-center shrink-0"
            >
              <X className="w-5 h-5" style={{ color: 'var(--pro-text-grey)' }} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label
                htmlFor="request-product-label"
                className="text-[13px] font-medium mb-1.5 block"
                style={{ color: 'var(--pro-text-muted)' }}
              >
                {t('pro.request.label', 'Produit demandé')}{' '}
                <span style={{ color: 'var(--pro-danger)' }}>*</span>
              </label>
              <input
                id="request-product-label"
                ref={labelRef}
                type="text"
                value={label}
                maxLength={200}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder={t(
                  'pro.request.label_placeholder',
                  'Ex. Disjoncteur différentiel 30mA 40A',
                )}
                aria-invalid={touched && invalid}
                className="pro-input w-full"
                style={
                  touched && invalid
                    ? { borderColor: 'var(--pro-danger)' }
                    : undefined
                }
              />
              {touched && invalid && (
                <p
                  className="text-[12px] mt-1.5"
                  style={{ color: 'var(--pro-danger)' }}
                >
                  {t('pro.request.label_required', 'Indiquez le produit souhaité.')}
                </p>
              )}
            </div>

            <div>
              <span
                className="text-[13px] font-medium mb-1.5 block"
                style={{ color: 'var(--pro-text-muted)' }}
              >
                {t('pro.request.quantity', 'Quantité')}
              </span>
              <QuantityStepper
                value={quantity}
                onChange={(n) => setQuantity(Math.max(1, n))}
                min={1}
                decreaseLabel={t('pro.cart.decrease', 'Diminuer')}
                increaseLabel={t('pro.cart.increase', 'Augmenter')}
                inputLabel={t('pro.request.quantity', 'Quantité')}
              />
            </div>

            <div>
              <label
                htmlFor="request-product-note"
                className="text-[13px] font-medium mb-1.5 block"
                style={{ color: 'var(--pro-text-muted)' }}
              >
                {t('pro.request.note', 'Précisions')}{' '}
                <span
                  className="font-normal"
                  style={{ color: 'var(--pro-text-light)' }}
                >
                  {t('pro.request.optional', '(facultatif)')}
                </span>
              </label>
              <textarea
                id="request-product-note"
                value={note}
                rows={3}
                maxLength={1000}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t(
                  'pro.request.note_placeholder',
                  'Marque, référence, calibre, couleur…',
                )}
                className="pro-input w-full resize-none"
                style={{ height: 'auto', paddingTop: 10, paddingBottom: 10 }}
              />
            </div>

            <div
              className="rounded-xl px-3 py-2.5 text-[12px] leading-relaxed"
              style={{
                background: 'var(--pro-grey-subtle)',
                color: 'var(--pro-text-grey)',
              }}
            >
              {t(
                'pro.request.hint',
                'Un produit demandé part sans prix : votre panier devient un devis, et le prix vous est communiqué une fois le produit trouvé.',
              )}
            </div>
          </div>

          <div
            className="px-5 py-4 flex items-center gap-2 justify-end"
            style={{ borderTop: '1px solid var(--pro-line)' }}
          >
            <button
              type="button"
              onClick={onClose}
              className="h-11 px-4 rounded-xl text-[14px] font-semibold hover:bg-neutral-50"
              style={{
                border: '1px solid var(--pro-border)',
                color: 'var(--pro-text-dark)',
                fontFamily: 'Figtree',
              }}
            >
              {t('pro.request.cancel', 'Annuler')}
            </button>
            <button
              type="button"
              onClick={submit}
              className="h-11 px-5 rounded-xl text-[14px] font-semibold text-white hover:opacity-90"
              style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
            >
              {t('pro.request.submit', 'Ajouter au panier')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
